import { useEffect, useState } from 'react';

import { toast } from 'sonner';

import {
  TEAM_EVENT_DESC_MAX,
  TEAM_EVENT_TITLE_MAX,
  todayIso,
} from '@lemoment/shared';
import type { CreateTeamEventRequest } from '@lemoment/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Switch,
  Textarea,
} from '@lemoment/ui-kit';

import { DateField } from '../../leaves';
import { useCreateTeamEvent } from '../hooks';

interface TeamEventFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TeamEventFormDialog = ({
  open,
  onOpenChange,
}: TeamEventFormDialogProps) => {
  const [title, setTitle] = useState('');
  const [allDay, setAllDay] = useState(true);
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('11:00');
  const [description, setDescription] = useState('');

  const createEvent = useCreateTeamEvent();
  const isPending = createEvent.isPending;

  // Reset the form whenever the dialog opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    setTitle('');
    setAllDay(true);
    setStartDate(todayIso());
    setEndDate(todayIso());
    setStartTime('10:00');
    setEndTime('11:00');
    setDescription('');
  }, [open]);

  // Keep the end date from drifting before the start date.
  const handleStartChange = (next: string) => {
    setStartDate(next);
    if (endDate < next) {
      setEndDate(next);
    }
  };

  const handleSubmit = () => {
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error('제목을 입력하세요.');
      return;
    }
    if (allDay && endDate < startDate) {
      toast.error('종료일은 시작일 이후여야 합니다.');
      return;
    }
    if (!allDay && endTime <= startTime) {
      toast.error('종료 시각은 시작 시각 이후여야 합니다.');
      return;
    }

    const body: CreateTeamEventRequest = allDay
      ? {
          title: trimmed,
          allDay: true,
          startDate,
          endDate,
          description: description.trim() || undefined,
        }
      : {
          title: trimmed,
          allDay: false,
          startDate,
          startTime,
          endTime,
          description: description.trim() || undefined,
        };

    createEvent.mutate(body, {
      onSuccess: () => {
        toast.success('팀 이벤트를 등록했습니다.');
        onOpenChange(false);
      },
      onError: () => toast.error('등록에 실패했습니다.'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-semibold tracking-tight">
            팀 이벤트 등록
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label className="eyebrow text-muted-foreground">제목</Label>
            <Input
              value={title}
              maxLength={TEAM_EVENT_TITLE_MAX}
              placeholder="예: 전사 워크샵"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
            <Label
              htmlFor="team-event-all-day"
              className="text-sm font-medium text-foreground"
            >
              종일
            </Label>
            <Switch
              id="team-event-all-day"
              checked={allDay}
              onCheckedChange={setAllDay}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="eyebrow text-muted-foreground">시작일</Label>
            <DateField value={startDate} onChange={handleStartChange} />
          </div>

          {allDay ? (
            <div className="flex flex-col gap-1.5">
              <Label className="eyebrow text-muted-foreground">종료일</Label>
              <DateField
                value={endDate}
                min={startDate}
                onChange={setEndDate}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label className="eyebrow text-muted-foreground">
                  시작 시각
                </Label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="eyebrow text-muted-foreground">
                  종료 시각
                </Label>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label className="eyebrow text-muted-foreground">설명</Label>
            <Textarea
              value={description}
              maxLength={TEAM_EVENT_DESC_MAX}
              placeholder="선택 입력"
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            취소
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending}
            className="bg-lemon text-lemon-foreground hover:bg-lemon/90"
          >
            등록
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
